import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
import AddEditProcessingFee from "./AddEditProcessingFee";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import ProcessingFeeActionButton from "./ProcessingFeeActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";
import UnauthorisedAccess from "../../UnauthorisedAccess";
import { useOrganization } from "../../lib/context/OrganisationContext";
import UploadProcessingFee from "./UploadProcessingFee";
import { InvoiceModuleConditon } from "../../lib/PermissionConditions";

const ProcessingFees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationData } = useOrganization();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [processingFeeList, setProcessingFeesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();

  const [processingFeeId, setProcessingFeeId] = useState(null);
  const [showAddEditProcessingFee, setShowAddEditProcessingFee] =
    useState(false);
  const [showProcessingFeeDelete, setShowProcessingFeeDelete] = useState(false);

  const toggleShowAddEditProcessingFee = () => {
    if (showAddEditProcessingFee) {
      setEdit(false);
      setProcessingFeeId(null);
    }
    setShowAddEditProcessingFee(!showAddEditProcessingFee);
  };

  const toggleShowDelete = () => {
    if (showProcessingFeeDelete) {
      setProcessingFeeId(null);
    }
    setShowProcessingFeeDelete(!showProcessingFeeDelete);
  };

  const fetchProcessingFees = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_PROCESSING_FEE_CODES}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const processingFeeList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < processingFeeList?.length; i++) {
            notSelectedIds.push(processingFeeList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setProcessingFeesList(processingFeeList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setProcessingFeesList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const processingFeesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: processingFeeList[i]?.id,
        code: processingFeeList[i]?.code ? processingFeeList[i]?.code : "",
        description: processingFeeList[i]?.description
          ? processingFeeList[i]?.description
          : "",
        processing_fee: processingFeeList[i]?.processing_fee
          ? processingFeeList[i]?.processing_fee_type === 0
            ? `$${processingFeeList[i]?.processing_fee}`
            : `${processingFeeList[i]?.processing_fee}%`
          : "",
        action: (
          <>
            <ProcessingFeeActionButton
              id={processingFeeList[i]?.id}
              setEdit={setEdit}
              setProcessingFeeId={setProcessingFeeId}
              toggleShowAddEditProcessingFee={toggleShowAddEditProcessingFee}
              toggleShowDelete={toggleShowDelete}
              used={processingFeeList[i]?.is_used ? true : false}
            />
          </>
        ),
      });
    }
    return items;
  };

  const processingFees = processingFeesGenerator(processingFeeList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "processing_fee",
      text: "Processing Fee",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchProcessingFees();
  }, [fetchProcessingFees]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchProcessingFees(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchProcessingFees(limit, page);
  };

  // Single COA Delete
  const handleProcessingFeeDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.PROCESSING_FEE_CODE_DELETE}/${processingFeeId}`);
      setLoading(false);
      fetchProcessingFees(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  return InvoiceModuleConditon(organizationData) ? (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Processing Fee Codes"}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <UploadProcessingFee
              fetchData={() => fetchProcessingFees(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />
            {/* Add ProcessingFee */}
            <ResponsiveDrawerForm
              description={""}
              title={
                edit ? "Edit Processing Fee Code" : "Add Processing Fee Code"
              }
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setProcessingFeeId(null);
                setShowAddEditProcessingFee(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Processing Fee</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditProcessingFee}
              buttonClassName={""}
              setOpen={setShowAddEditProcessingFee}
              write={true}
            >
              <AddEditProcessingFee
                edit={edit}
                toggleShowAddEditProcessingFee={toggleShowAddEditProcessingFee}
                id={processingFeeId}
                trustId={trustId}
                fetchProcessingFeeData={() => fetchProcessingFees(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={processingFees}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Processing Fee Code"}
        open={showProcessingFeeDelete}
        setOpen={setShowProcessingFeeDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete processing fee?"}
        okAction={async () => {
          handleProcessingFeeDelete();
          toggleShowDelete();
          // fetchProcessingFees();
        }}
        cancelAction={() => {
          setProcessingFeeId(null);
          setShowProcessingFeeDelete(!showProcessingFeeDelete);
        }}
      />
    </>
  ) : (
    <UnauthorisedAccess />
  );
};

export default ProcessingFees;
